import classNames from "classnames";
import { useMemo, useState } from "react";
import { Nav, OverlayTrigger, Tab, Tooltip } from "react-bootstrap";
import { TransactionRuleManagement } from "@components/transactionRules/TransactionRuleManagementContext/TransactionRuleManagement";
import { useCategoryLabelGetter } from "../../../hooks/useCategoryLabelGetter";
import { useEntities } from "../../../hooks/useEntities";
import { useFinancialAccounts } from "../../../hooks/useFinancialAccounts";
import {
    useWorkspaceRuleGroups,
    useDefaultAccountingRules,
} from "../../../api/rules.api";
import { FiltersTypes } from "../../../common/types/filters/general";
import { useWorkspaceAccounts } from "../../../api/account.api";
import { useClasses } from "../../../api/class.api";
import { RulesPageHeader } from "./RulesPageHeader";
import styles from "./RulesPage.module.scss";
import { buildRulesItems } from "./utils";
import { RulesTable } from "./RulesTable/RulesTable";

export const RulesPage: React.FC = () => {
    const categoryLabelGetter = useCategoryLabelGetter();
    const entities = useEntities();
    const financialAccounts = useFinancialAccounts();
    const { classesMap } = useClasses();
    const accounts = useWorkspaceAccounts();
    const [activeTab, setActiveTab] = useState<"transaction" | "accounting">(
        "transaction",
    );

    const ruleGroups = useWorkspaceRuleGroups();

    const [filters, setFilters] = useState<
        Pick<FiltersTypes, "entityIds" | "rulesEnabledOnly">
    >({
        entityIds: entities.map((entity) => entity.id),
        rulesEnabledOnly: undefined,
    });

    const defaultAccountingRules = useDefaultAccountingRules();

    const [searchQuery, setSearchQuery] = useState("");

    const filteredRuleGroups = useMemo(
        () =>
            buildRulesItems({
                search: searchQuery,
                categoryLabelGetter,
                ruleGroups: ruleGroups?.data?.ruleGroups,
                entities,
                financialAccounts,
                counterparties: ruleGroups?.data?.counterparties ?? [],
                filters,
                accounts: accounts.data ?? [],
                classesMap,
                activeTab,
            }),
        [
            searchQuery,
            categoryLabelGetter,
            ruleGroups,
            entities,
            financialAccounts,
            filters,
            accounts,
            classesMap,
            activeTab,
        ],
    );

    const accountingRuleGroups = useMemo(
        () =>
            buildRulesItems({
                search: searchQuery,
                categoryLabelGetter,
                ruleGroups: defaultAccountingRules?.data?.ruleGroup
                    ? [defaultAccountingRules?.data?.ruleGroup]
                    : [],
                entities,
                financialAccounts,
                counterparties: ruleGroups?.data?.counterparties ?? [],
                filters,
                accounts: accounts.data ?? [],
                classesMap,
                activeTab,
            }),
        [
            searchQuery,
            categoryLabelGetter,
            defaultAccountingRules?.data?.ruleGroup,
            entities,
            financialAccounts,
            ruleGroups?.data?.counterparties,
            filters,
            accounts,
            classesMap,
            activeTab,
        ],
    );

    return (
        <div className={classNames(styles.rules, "settings__content")}>
            <TransactionRuleManagement>
                <Tab.Container activeKey={activeTab}>
                    <RulesPageHeader
                        setSearch={setSearchQuery}
                        search={searchQuery}
                        filters={filters}
                        onFiltersChange={setFilters}
                        activeTab={activeTab}
                        navigation={
                            <Nav variant="tabs" className={styles.tabs}>
                                <Nav.Item>
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip id="btn-tooltip-transaction-nav">
                                                Apply custom changes to
                                                transactions on sync
                                            </Tooltip>
                                        }
                                    >
                                        <Nav.Link
                                            active={activeTab === "transaction"}
                                            onClick={() =>
                                                setActiveTab("transaction")
                                            }
                                        >
                                            Transactions
                                        </Nav.Link>
                                    </OverlayTrigger>
                                </Nav.Item>
                                <Nav.Item>
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip id="btn-tooltip-accounting-nav">
                                                Map transactions to the Chart of
                                                Accounts
                                            </Tooltip>
                                        }
                                    >
                                        <Nav.Link
                                            active={activeTab === "accounting"}
                                            onClick={() =>
                                                setActiveTab("accounting")
                                            }
                                        >
                                            Accounting
                                        </Nav.Link>
                                    </OverlayTrigger>
                                </Nav.Item>
                            </Nav>
                        }
                    />

                    <RulesTable
                        groups={[...(filteredRuleGroups ?? [])]}
                        accountingGroup={
                            activeTab === "accounting"
                                ? accountingRuleGroups?.[0]
                                : undefined
                        }
                        counterparties={ruleGroups?.data?.counterparties ?? []}
                        transactionCounts={
                            ruleGroups?.data?.transactionCounts ?? {}
                        }
                    />
                </Tab.Container>
            </TransactionRuleManagement>
        </div>
    );
};
